import React, { useState, useEffect } from "react";
import { X, ChevronRight, ChevronLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const TOUR_STEPS = [
  {
    id: "class-tabs",
    type: "highlight",
    target: "[data-tutorial='class-tabs']",
    title: "Class Tabs",
    description: "Switch between your classes using these tabs. Each class has its own assignments, documents, and virtual rooms organized separately.",
    position: "bottom",
  },
  {
    id: "timeline",
    type: "highlight",
    target: "[data-tutorial='timeline']",
    title: "Timeline & Calendar",
    description: "View all your assignments across ALL classes in calendar view. Track what's upcoming, in progress, and completed with filters.",
    position: "top",
  },
];

const STORAGE_KEY = "whipt-onboarding-v1";

export function useOnboardingTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(true);
  const [checkedStorage, setCheckedStorage] = useState(false);

  useEffect(() => {
    const checkTourStatus = async () => {
      try {
        // Get user from localStorage
        const userStr = localStorage.getItem('whipt_user');
        if (!userStr) {
          setHasSeenTour(true);
          setCheckedStorage(true);
          return;
        }

        const user = JSON.parse(userStr);
        
        // Check database for has_seen_tour flag
        const { db } = await import('@/database/Database');
        const result = await db.query(
          'SELECT has_seen_tour FROM users WHERE id = ?',
          [user.id]
        );

        if (result && result.length > 0) {
          const hasSeen = result[0].has_seen_tour === 1;
          setHasSeenTour(hasSeen);
          
          // Also check localStorage as fallback
          if (!hasSeen) {
            const localSeen = localStorage.getItem(STORAGE_KEY);
            if (localSeen === "true") {
              // Update database to match localStorage
              await db.run(
                'UPDATE users SET has_seen_tour = 1 WHERE id = ?',
                [user.id]
              );
              setHasSeenTour(true);
            }
          }
        } else {
          // Fallback to localStorage
          const seen = localStorage.getItem(STORAGE_KEY);
          setHasSeenTour(seen === "true");
        }
      } catch (e) {
        console.error('Failed to check tour status:', e);
        // Fallback to localStorage
        const seen = localStorage.getItem(STORAGE_KEY);
        setHasSeenTour(seen === "true");
      }
      setCheckedStorage(true);
    };

    checkTourStatus();
  }, []);

  const startTour = () => {
    setCurrentStep(0);
    setIsActive(true);
  };

  const nextStep = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      endTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const endTour = async () => {
    setIsActive(false);
    setCurrentStep(0);
    
    try {
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, "true");
      
      // Update database
      const userStr = localStorage.getItem('whipt_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const { db } = await import('@/database/Database');
        await db.run(
          'UPDATE users SET has_seen_tour = 1 WHERE id = ?',
          [user.id]
        );
        console.log('✅ Tutorial completion saved to database');
      }
    } catch (e) {
      console.error('Failed to save tour completion:', e);
    }
    setHasSeenTour(true);
  };

  const skipTour = async () => {
    try {
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, "true");
      
      // Update database
      const userStr = localStorage.getItem('whipt_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const { db } = await import('@/database/Database');
        await db.run(
          'UPDATE users SET has_seen_tour = 1 WHERE id = ?',
          [user.id]
        );
        console.log('✅ Tutorial skip saved to database');
      }
    } catch (e) {
      console.error('Failed to save tour skip:', e);
    }
    setHasSeenTour(true);
    setIsActive(false);
  };

  return {
    isActive,
    currentStep,
    hasSeenTour,
    checkedStorage,
    steps: TOUR_STEPS,
    startTour,
    nextStep,
    prevStep,
    endTour,
    skipTour,
  };
}

export default function FeatureTutorial() {
  // Prevent multiple simultaneous tutorial instances (only one overlay allowed)
  const alreadyActive = (window as any).__whiptTutorialActive;
  const [enabled, setEnabled] = useState(() => !alreadyActive);

  useEffect(() => {
    if (!enabled) return;
    (window as any).__whiptTutorialActive = true;
    return () => {
      (window as any).__whiptTutorialActive = false;
    };
  }, [enabled]);

  if (!enabled) return null;
  const tour = useOnboardingTour();
  const { isActive, currentStep, steps, nextStep, prevStep, endTour, hasSeenTour, checkedStorage } = tour;
  const step = steps[currentStep];
  const [targetRect, setTargetRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  // Allow ESC to exit if the user gets stuck
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") endTour();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, endTour]);

  // Auto-start tour for new users
  useEffect(() => {
    if (checkedStorage && !hasSeenTour) {
      tour.startTour();
    }
  }, [checkedStorage, hasSeenTour]);

  useEffect(() => {
    if (!isActive || step.type !== "highlight") {
      setTargetRect(null);
      return;
    }

    // Try to find the target element, with timeout to allow page to render
    const attemptToFindTarget = () => {
      const target = document.querySelector(step.target);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        
        const updateRect = () => {
          const rect = target.getBoundingClientRect();
          setTargetRect({
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
          });
        };
        
        updateRect();
        window.addEventListener('resize', updateRect);
        window.addEventListener('scroll', updateRect);
        
        return () => {
          window.removeEventListener('resize', updateRect);
          window.removeEventListener('scroll', updateRect);
        };
      } else {
        // If target not found, skip this step after 2 seconds
        console.warn(`Tutorial: Target not found: ${step.target}`);
        const skipTimer = setTimeout(() => {
          console.log(`Skipping step ${currentStep + 1} - element not found`);
          nextStep();
        }, 2000);
        return () => clearTimeout(skipTimer);
      }
    };

    // Wait a bit for the DOM to settle
    const initTimer = setTimeout(attemptToFindTarget, 300);
    return () => clearTimeout(initTimer);
  }, [isActive, currentStep, step]);

  if (!isActive) return null;

  const isModal = step.type === "modal";

  if (isModal) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) endTour(); }}>
        <DialogContent className="max-w-md bg-black border-2 border-amber-400/50 shadow-2xl shadow-amber-500/20">
          <DialogHeader>
            <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 via-yellow-600 to-amber-700 flex items-center justify-center shadow-lg shadow-amber-500/50">
              <span className="text-4xl font-bold text-black">W</span>
            </div>
            <DialogTitle className="text-center text-2xl text-white font-bold">
              {step.title}
            </DialogTitle>
            <DialogDescription className="text-center text-slate-300 text-base">
              {step.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 justify-center sm:justify-center">
            {currentStep === 0 ? (
              <>
                <Button 
                  variant="ghost" 
                  onClick={endTour}
                  className="text-slate-400 hover:text-white hover:bg-white/10"
                >
                  Skip Tour
                </Button>
                <Button 
                  onClick={nextStep}
                  className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold"
                >
                  Start Tour
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            ) : (
              <Button 
                onClick={endTour}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold"
              >
                Get Started
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Highlight step - show spotlight and tooltip
  const getTooltipPosition = () => {
    const target = document.querySelector(step.target);
    if (!target) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    
    const rect = target.getBoundingClientRect();
    const pos = step.position || "bottom";
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const offset = 20;
    
    let top, left, transform;
    
    switch (pos) {
      case "bottom":
        top = rect.bottom + offset;
        left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
        transform = "translate(0, 0)";
        break;
      case "top":
        top = rect.top - tooltipHeight - offset;
        left = Math.max(16, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 16));
        transform = "translate(0, 0)";
        break;
      case "left":
        top = Math.max(16, Math.min(rect.top + rect.height / 2 - tooltipHeight / 2, window.innerHeight - tooltipHeight - 16));
        left = rect.left - tooltipWidth - offset;
        transform = "translate(0, 0)";
        break;
      case "right":
        top = Math.max(16, Math.min(rect.top + rect.height / 2 - tooltipHeight / 2, window.innerHeight - tooltipHeight - 16));
        left = rect.right + offset;
        transform = "translate(0, 0)";
        break;
      default:
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        transform = "translate(0, 0)";
    }
    
    if (left < 16) left = 16;
    if (left + tooltipWidth > window.innerWidth - 16) left = window.innerWidth - tooltipWidth - 16;
    if (top < 16) top = 16;
    if (top + tooltipHeight > window.innerHeight - 16) top = window.innerHeight - tooltipHeight - 16;
    
    return { top, left, transform };
  };

  const position = getTooltipPosition();

  return (
    <>
      {/* Spotlight Overlay with SVG cutout */}
      <div className="fixed inset-0 z-[9998] pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left}
                  y={targetRect.top}
                  width={targetRect.width}
                  height={targetRect.height}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.85)"
            mask="url(#spotlight-mask)"
          />
          {/* Glowing gold border around spotlight */}
          {targetRect && (
            <rect
              x={targetRect.left}
              y={targetRect.top}
              width={targetRect.width}
              height={targetRect.height}
              rx="12"
              fill="none"
              stroke="rgba(245, 158, 11, 0.6)"
              strokeWidth="3"
            />
          )}
        </svg>
      </div>
      
      {/* Tooltip */}
      <div
        className="fixed z-[9999] p-5 rounded-xl shadow-2xl w-80 bg-black border-2 border-amber-400/50 shadow-amber-500/20"
        style={{
          top: position.top,
          left: position.left,
          transform: position.transform,
        }}
      >
        <button
          onClick={endTour}
          className="absolute top-3 right-3 p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <h3 className="font-semibold text-lg mb-2 text-amber-400">
          {step.title}
        </h3>
        <p className="text-sm mb-4 leading-relaxed text-slate-300">
          {step.description}
        </p>
        
        <div className="flex items-center justify-between">
          <span className="text-xs text-amber-600">
            {currentStep + 1} / {steps.length}
          </span>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={prevStep}
                className="text-slate-300 hover:text-white hover:bg-white/10"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button 
              size="sm" 
              onClick={nextStep}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold"
            >
              {currentStep === steps.length - 1 ? "Finish" : "Next"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
